import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { WizardState, WizardStep } from './types';
import { loadQuota, loadBrandProfiles, generateBrandProfile, generatePage } from './thunks';

const initialState: WizardState = {
  currentStep: 'buildMethod',
  quota: null,
  quotaLoading: false,
  brandProfile: null,
  brandProfileStatus: 'idle',
  brandProfileSessionId: null,
  brandProfileError: null,
  selectedStyleId: null,
  selectedGoal: null,
  pageDescription: '',
  generationJobId: null,
  generationStatus: 'idle',
  generationError: null,
  generatedPageId: null,
};

const wizardSlice = createSlice({
  name: 'wizard',
  initialState,
  reducers: {
    setStep(state, action: PayloadAction<WizardStep>) {
      state.currentStep = action.payload;
    },
    setSelectedStyleId(state, action: PayloadAction<string | null>) {
      state.selectedStyleId = action.payload;
    },
    setSelectedGoal(state, action: PayloadAction<string>) {
      state.selectedGoal = action.payload;
    },
    setPageDescription(state, action: PayloadAction<string>) {
      state.pageDescription = action.payload;
    },
    resetWizard() {
      return initialState;
    },
    resetGenerationError(state) {
      state.generationStatus = 'idle';
      state.generationError = null;
    },
    resetBrandProfileError(state) {
      state.brandProfileStatus = 'idle';
      state.brandProfileError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Quota
      .addCase(loadQuota.pending, (state) => {
        state.quotaLoading = true;
      })
      .addCase(loadQuota.fulfilled, (state, action) => {
        state.quotaLoading = false;
        state.quota = action.payload;
      })
      .addCase(loadQuota.rejected, (state) => {
        state.quotaLoading = false;
      })

      // Brand profiles
      .addCase(loadBrandProfiles.pending, (state) => {
        state.brandProfileStatus = 'loading';
      })
      .addCase(loadBrandProfiles.fulfilled, (state, action) => {
        const profiles = action.payload;
        if (profiles.length > 0) {
          state.brandProfile = profiles[0];
          state.brandProfileStatus = 'ready';
          if (profiles[0].globalStyleId) {
            state.selectedStyleId = profiles[0].globalStyleId;
          }
        }
      })
      .addCase(loadBrandProfiles.rejected, (state, action) => {
        state.brandProfileStatus = 'failed';
        state.brandProfileError = action.error.message ?? 'Failed to load brand profiles';
      })

      // Generate brand profile
      .addCase(generateBrandProfile.pending, (state) => {
        state.brandProfileStatus = 'polling';
        state.brandProfileError = null;
      })
      .addCase(generateBrandProfile.fulfilled, (state, action) => {
        state.brandProfileStatus = 'ready';
        state.brandProfile = action.payload;
        if (action.payload.globalStyleId) {
          state.selectedStyleId = action.payload.globalStyleId;
        }
      })
      .addCase(generateBrandProfile.rejected, (state, action) => {
        state.brandProfileStatus = 'failed';
        state.brandProfileError = action.payload ?? 'Brand profile generation failed';
      })

      // Generate page
      .addCase(generatePage.pending, (state) => {
        state.generationStatus = 'pending';
        state.generationError = null;
      })
      .addCase(generatePage.fulfilled, (state, action) => {
        state.generationStatus = 'completed';
        state.generatedPageId = action.payload;
      })
      .addCase(generatePage.rejected, (state, action) => {
        state.generationStatus = 'failed';
        state.generationError = action.payload ?? 'Page generation failed, please try again.';
      });
  },
});

export const {
  setStep,
  setSelectedStyleId,
  setSelectedGoal,
  setPageDescription,
  resetWizard,
  resetGenerationError,
  resetBrandProfileError,
} = wizardSlice.actions;

export default wizardSlice.reducer;
